/**
 * PrimaryInfoSection — the 3-column primary information area at the top of
 * the document modal: document info (left), contact/location/warehouse
 * selection (middle), and summary card (right).
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 */

import React from 'react';
import { Link, X, User, Search, RefreshCw, Building, MapPin, ChevronDown, Package } from 'lucide-react';
import {
  type DocumentConfig,
  type DocumentFormState,
} from '../documentModalHelpers';
import {
  type Document,
  type DocumentType,
} from '@/hooks/useDocumentsWorkItems';
import { type Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import { type LocationResult } from '../hooks/useContactSearch';
import { FinanceStatusBadge, FinanceSummaryCard, type FinanceSummaryRow } from '../DocumentModalShared';
import UnifiedContactLookup, {
  type UnifiedLookupContact,
  type UnifiedLookupLocation,
  type UnifiedLookupContactPerson,
} from '@/components/shared/UnifiedContactLookup';
import { type RiskFlag } from '@/lib/finance/riskDetection';
import { formatDateSAST } from '@/lib/sastTime';
import { type UnifiedSearchResult } from '../hooks/useContactSearch';
import { DatePickerInput, TimePickerInput } from '@/components/shared/DateTimeInput';

// Document types that carry monetary totals and therefore show the
// subtotal/tax/total summary card. Operational docs (delivery notes,
// picking slips, stock moves) and non-monetary specialty docs are excluded.
const FINANCE_SUMMARY_DOC_TYPES = new Set<DocumentType>([
  'din',
  'invoice',
  'proposal',
  'quote',
  'proforma',
  'credit_note',
  'sales_order',
  'billing_submission',
  'expense',
  'supplier_invoice',
  'supplier_credit',
  'supplier_quote',
  'purchase_order',
  'debit_note',
  'purchase_return',
]);

export interface PrimaryInfoSectionProps {
  showPrimaryInfoSection: boolean;
  isDarkMode: boolean;
  isCompactMobileDoc: boolean;
  isEditing: boolean;
  isExpenseDoc: boolean;
  isPurchaseDocument: boolean;
  doc_type: DocumentType;
  config: DocumentConfig;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  // Parent document linking
  canLinkParent: boolean;
  upstreamDocTypes: string[];
  docTypeLabels: Record<string, string>;
  linkedParentDocument: Document | null;
  showParentSearch: boolean;
  setShowParentSearch: React.Dispatch<React.SetStateAction<boolean>>;
  loadParentDocuments: (query?: string) => void;
  parentDocumentSearchQuery: string;
  setParentDocumentSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  searchingParent: boolean;
  parentDocumentResults: Document[];
  setParentDocumentResults: React.Dispatch<React.SetStateAction<Document[]>>;
  handleSelectParentDocument: (doc: Document) => void;
  handleClearParentDocument: () => void;
  // Currency
  currencySymbol: string;
  formatCurrency: (value: number | string | undefined | null) => string;
  formatCurrencyWithSymbol: (value: number | string | undefined | null) => string;
  activeCurrency: string;
  // Date formatting
  formatDateSAST: typeof formatDateSAST;
  // Layout flags
  useCompactPrimaryDateLayout: boolean;
  useContactSectionAccordion: boolean;
  showExpandedContactSection: boolean;
  // Contact section
  isContactSectionExpanded: boolean;
  setIsContactSectionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedContactName: string;
  selectedLocationSummary: string;
  selectedContactPersonSummary: string;
  selectedContact: Contact | null;
  // Unified contact lookup
  unifiedContacts: UnifiedLookupContact[];
  unifiedLocations: UnifiedLookupLocation[];
  unifiedContactPeople: UnifiedLookupContactPerson[];
  handleUnifiedContactChange: (id: number | null, _contactName?: string) => void;
  handleUnifiedLocationChange: (id: number | null) => void;
  handleUnifiedPersonChange: (id: number | null, _name: string) => void;
  handleUnifiedSelect: (result: UnifiedSearchResult) => Promise<void>;
  handleUnifiedLocationCreated: (newLocation: UnifiedLookupLocation) => void;
  handleUnifiedPersonCreated: (newPerson: UnifiedLookupContactPerson) => void;
  loadingContacts: boolean;
  loadingLocations: boolean;
  loadingContactPeople: boolean;
  // Risk assessment
  riskAssessment: {
    riskFlags: RiskFlag[];
  };
  // Stakeholder modals
  setShowStakeholderOverview: React.Dispatch<React.SetStateAction<boolean>>;
  setShowStakeholderModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** Fired after a contact is created via the inline QuickContactModal.
   *  DocumentModal uses this to auto-open the stakeholder overview for the
   *  newly created supplier (e.g. to review payment terms). */
  onQuickContactCreated?: (result: import('@/components/contacts/QuickContactModal').QuickContactResult) => void;
  // Warehouse
  selectedWarehouse: LocationResult | null;
  handleWarehouseSelect: (warehouse: LocationResult | null) => void;
  locations: LocationResult[];
  /** External-system reference — only set when a publish adapter is connected. */
  externalReference?: { value: string | null; system: string } | null;
  // Summary
  summaryRows: FinanceSummaryRow[];
  summaryFooterRows: FinanceSummaryRow[];
  // Total lock mode
  totalLocked: boolean;
  onToggleLock: () => void;
  hasDiscrepancy: boolean;
  onAddBalancingLine: () => void;
}

export function PrimaryInfoSection({
  showPrimaryInfoSection,
  isDarkMode,
  isCompactMobileDoc,
  isEditing,
  isExpenseDoc,
  isPurchaseDocument,
  doc_type,
  config,
  formData,
  setFormData,
  canLinkParent,
  upstreamDocTypes,
  docTypeLabels,
  linkedParentDocument,
  showParentSearch,
  setShowParentSearch,
  loadParentDocuments,
  parentDocumentSearchQuery,
  setParentDocumentSearchQuery,
  searchingParent,
  parentDocumentResults,
  setParentDocumentResults,
  handleSelectParentDocument,
  handleClearParentDocument,
  currencySymbol,
  formatCurrency,
  formatCurrencyWithSymbol,
  activeCurrency,
  formatDateSAST: fmtDateSAST,
  useCompactPrimaryDateLayout,
  useContactSectionAccordion,
  showExpandedContactSection,
  isContactSectionExpanded,
  setIsContactSectionExpanded,
  selectedContactName,
  selectedLocationSummary,
  selectedContactPersonSummary,
  selectedContact,
  unifiedContacts,
  unifiedLocations,
  unifiedContactPeople,
  handleUnifiedContactChange,
  handleUnifiedLocationChange,
  handleUnifiedPersonChange,
  handleUnifiedSelect,
  handleUnifiedLocationCreated,
  handleUnifiedPersonCreated,
  loadingContacts,
  loadingLocations,
  loadingContactPeople,
  riskAssessment,
  setShowStakeholderOverview,
  setShowStakeholderModal,
  onQuickContactCreated,
  selectedWarehouse,
  handleWarehouseSelect,
  locations,
  externalReference,
  summaryRows,
  summaryFooterRows,
  totalLocked,
  onToggleLock,
  hasDiscrepancy,
  onAddBalancingLine,
}: PrimaryInfoSectionProps) {
  // The finance summary card (subtotal / tax / total) is shown for all
  // money-carrying documents — invoices, quotes, credit notes, expenses —
  // so imported/externally-synced docs display their totals too. The
  // DIN-specific lock & discrepancy reconciliation stays DIN-only (gated
  // inside useDocumentTotals via totalLockAvailable).
  const showFinanceSummary = FINANCE_SUMMARY_DOC_TYPES.has(doc_type);
  if (!showPrimaryInfoSection) return null;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 p-3 sm:p-6 border-b ${isDarkMode
      ? 'bg-gray-800/50 border-gray-700'
      : 'bg-gray-50 border-gray-200'
      }`}>
      {/* Left Column - Document Info */}
      <div className="md:col-span-4 space-y-4">
        {/* Link Parent Document Section - Only show for downstream documents */}
        {canLinkParent && !isEditing && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Link from {upstreamDocTypes.map(t => docTypeLabels[t]).join(' / ')}
            </label>

            {linkedParentDocument ? (
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${isDarkMode
                ? 'bg-blue-900/30 border-blue-700 text-blue-300'
                : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}>
                <div className="flex items-center gap-2">
                  <Link size={14} />
                  <span className="text-sm font-medium">
                    {docTypeLabels[linkedParentDocument.doc_type || '']} {linkedParentDocument.doc_number}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`}>
                    ({currencySymbol} {formatCurrency(linkedParentDocument.grand_total || 0)})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClearParentDocument}
                  className={`p-1 rounded hover:bg-blue-200/50 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}
                >
                  <X size={14} />
                </button>
              </div>
            ) : !formData.contact_id ? (
              // Show message to select customer first
              <div className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode
                ? 'bg-gray-700/50 border-gray-600 text-gray-400'
                : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}>
                <span className="flex items-center gap-2">
                  <User size={14} />
                  Select a {isPurchaseDocument ? 'supplier' : 'customer'} first to search linked documents
                </span>
              </div>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowParentSearch(!showParentSearch);
                    // Load all documents when opening dropdown
                    if (!showParentSearch) {
                      loadParentDocuments();
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center gap-2 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                    }`}
                >
                  <Search size={14} className="text-gray-400" />
                  <span>Search {formData.contact_name ? `${formData.contact_name}'s` : ''} {upstreamDocTypes.map(t => docTypeLabels[t]).join('/')}</span>
                </button>

                {showParentSearch && (
                  <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-xl border ${isDarkMode
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                    }`}>
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={parentDocumentSearchQuery}
                          onChange={(e) => {
                            setParentDocumentSearchQuery(e.target.value);
                            loadParentDocuments(e.target.value);
                          }}
                          placeholder={`Filter ${formData.contact_name || 'customer'}'s documents...`}
                          className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                            }`}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {searchingParent ? (
                        <div className="p-4 text-center text-gray-500">
                          <RefreshCw size={18} className="animate-spin mx-auto mb-2" />
                          Searching...
                        </div>
                      ) : parentDocumentResults.length > 0 ? (
                        parentDocumentResults.map((doc) => (
                          <button
                            key={doc.id}
                            type="button"
                            onClick={() => handleSelectParentDocument(doc)}
                            className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${isDarkMode
                              ? 'hover:bg-gray-700 border-b border-gray-700 last:border-0'
                              : 'hover:bg-gray-50 border-b border-gray-100 last:border-0'
                              }`}
                          >
                            <div>
                              <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {docTypeLabels[doc.doc_type || '']} {doc.doc_number}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {doc.contact_name || 'No contact'} • {doc.issued_at ? fmtDateSAST(doc.issued_at) : 'No date'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`font-mono text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                {currencySymbol} {formatCurrency(doc.grand_total || 0)}
                              </div>
                              <FinanceStatusBadge status={doc.status || 'draft'} isDark={isDarkMode} itemType={doc.doc_type || doc_type} />
                            </div>
                          </button>
                        ))
                      ) : parentDocumentSearchQuery ? (
                        <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No documents found matching "{parentDocumentSearchQuery}"
                        </div>
                      ) : (
                        <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No {upstreamDocTypes.map(t => docTypeLabels[t]).join('/')} found for {formData.contact_name || 'this contact'}
                        </div>
                      )}
                    </div>
                    <div className={`p-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowParentSearch(false);
                          setParentDocumentSearchQuery('');
                          setParentDocumentResults([]);
                        }}
                        className={`w-full px-3 py-2 text-sm rounded-lg ${isDarkMode
                          ? 'text-gray-400 hover:bg-gray-700'
                          : 'text-gray-600 hover:bg-gray-100'
                          }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {useCompactPrimaryDateLayout ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  {config.labels.numberField}
                </label>
                <input
                  type="text"
                  value={formData.doc_number || ''}
                  readOnly
                  className={`w-full px-3 py-2 border rounded-lg text-sm cursor-not-allowed ${isDarkMode
                    ? 'bg-gray-800 border-gray-700 text-gray-400'
                    : 'bg-gray-100 border-gray-200 text-gray-600'
                    }`}
                  placeholder={config.labels.numberPlaceholder}
                  title="Document numbers are auto-generated and cannot be modified"
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Reference
                </label>
                <input
                  type="text"
                  value={formData.reference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  placeholder="Reference number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  {config.labels.dateField}
                </label>
                <input
                  type="date"
                  value={formData.issued_at || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, issued_at: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiry_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={isCompactMobileDoc ? 'grid grid-cols-2 gap-3' : 'space-y-4'}>
            <div className="min-w-0">
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                {config.labels.numberField}
              </label>
              <input
                type="text"
                value={formData.doc_number || ''}
                readOnly
                className={`w-full px-3 py-2 border rounded-lg text-sm cursor-not-allowed ${isDarkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-400'
                  : 'bg-gray-100 border-gray-200 text-gray-600'
                  }`}
                placeholder={config.labels.numberPlaceholder}
                title="Document numbers are auto-generated and cannot be modified"
              />
            </div>

            <div className="min-w-0">
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                {config.labels.dateField}
              </label>
              <input
                type="date"
                value={formData.issued_at || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, issued_at: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>
          </div>
        )}

        {config.fields.showTransactionDate && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Transaction Date
            </label>
            {/* Stored as `YYYY-MM-DDTHH:mm`, or bare `YYYY-MM-DD` while the
                slip time is missing — the reviewer picks it so the
                transaction time is reliable for duplicate detection. */}
            {(() => {
              const rawValue = String(formData.transaction_date || '');
              const tIdx = rawValue.indexOf('T');
              const rawDate = tIdx >= 0 ? rawValue.slice(0, tIdx) : rawValue;
              const datePart = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : '';
              const rawTime = tIdx >= 0 ? rawValue.slice(tIdx + 1, tIdx + 6) : '';
              const timePart = /^\d{2}:\d{2}$/.test(rawTime) ? rawTime : '';
              return (
                <div className="grid grid-cols-2 gap-2">
                  <DatePickerInput
                    value={datePart}
                    onChange={(d) => setFormData(prev => ({ ...prev, transaction_date: timePart ? `${d}T${timePart}` : d }))}
                    isDark={isDarkMode}
                  />
                  <TimePickerInput
                    value={timePart}
                    onChange={(t) => setFormData(prev => ({ ...prev, transaction_date: datePart ? `${datePart}T${t}` : `T${t}` }))}
                    isDark={isDarkMode}
                  />
                </div>
              );
            })()}
          </div>
        )}

        {config.fields.showDueDate && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Due Date
            </label>
            <input
              type="date"
              value={formData.due_date || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
            />
          </div>
        )}

        {(config.fields.showExpiryDate || config.fields.showReference) && !useCompactPrimaryDateLayout && (
          <div className={isCompactMobileDoc && config.fields.showExpiryDate && config.fields.showReference ? 'grid grid-cols-2 gap-3' : 'space-y-4'}>
            {config.fields.showExpiryDate && (
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiry_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
              </div>
            )}

            {config.fields.showReference && (
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Reference
                </label>
                <input
                  type="text"
                  value={formData.reference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  placeholder="Reference number"
                />
              </div>
            )}
          </div>
        )}

        {config.fields.showCustomerPO && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Customer PO #
            </label>
            <input
              type="text"
              value={formData.customer_po_number || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, customer_po_number: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
              placeholder="PO number"
            />
          </div>
        )}

        {/* External reference — the Id this document carries in the connected
            accounting system (QBO, Xero…). Only rendered when an adapter is
            connected, matching PublishSyncDashboard's adapter gating. */}
        {externalReference && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              External Ref{externalReference.system ? ` · ${externalReference.system}` : ''}
            </label>
            <input
              type="text"
              value={externalReference.value || 'Not published'}
              readOnly
              className={`w-full px-3 py-2 border rounded-lg text-sm cursor-not-allowed ${isDarkMode
                ? 'bg-gray-800 border-gray-700 text-gray-400'
                : 'bg-gray-100 border-gray-200 text-gray-600'
                }`}
              title={externalReference.value ? 'Id of this document in the external accounting system' : 'This document has not been published to the external system'}
            />
          </div>
        )}
      </div>

      {/* Middle Column - Contact, Location & Person Selection */}
      <div className={showFinanceSummary ? 'md:col-span-4 space-y-3' : 'md:col-span-8 space-y-3'} data-tour-id={isExpenseDoc ? 'expense-supplier-match' : 'doc-modal-contact-lookup'}>
        {useContactSectionAccordion && (
          <div className={`overflow-hidden rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'
            }`}>
            <button
              type="button"
              onClick={() => {
                setIsContactSectionExpanded(prev => !prev);
              }}
              className="w-full px-3 py-3 text-left"
              aria-expanded={isContactSectionExpanded}
              aria-label={`${isContactSectionExpanded ? 'Collapse' : 'Expand'} customer details section`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Customer Details
                      </div>
                      <div className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                        {isContactSectionExpanded ? 'Tap to collapse' : 'Tap to review or edit'}
                      </div>
                    </div>
                    <div className={`rounded-xl p-2 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isContactSectionExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    {[
                      {
                        key: 'contact',
                        label: config.labels.contactLabel,
                        value: selectedContactName,
                        icon: Building,
                        tone: isDarkMode ? 'bg-blue-900/30 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-700',
                      },
                      {
                        key: 'location',
                        label: 'Location',
                        value: selectedLocationSummary,
                        icon: MapPin,
                        tone: isDarkMode ? 'bg-green-900/30 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700',
                      },
                      {
                        key: 'person',
                        label: 'Person',
                        value: selectedContactPersonSummary,
                        icon: User,
                        tone: isDarkMode ? 'bg-purple-900/30 border-purple-800 text-purple-200' : 'bg-purple-50 border-purple-200 text-purple-700',
                      },
                    ].map(({ key, label, value, icon: Icon, tone }) => (
                      <div
                        key={key}
                        className={`min-w-[8.5rem] flex-1 rounded-xl border px-3 py-2 ${tone}`}
                      >
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">
                          <Icon className="h-3 w-3" />
                          <span>{label}</span>
                        </div>
                        <div className="mt-1 truncate text-xs font-semibold">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}

        {showExpandedContactSection && (
          <>
            <UnifiedContactLookup
              contacts={unifiedContacts}
              locations={unifiedLocations}
              contactPeople={unifiedContactPeople}
              selectedContactId={formData.contact_id ?? null}
              selectedLocationId={formData.location_id ?? null}
              selectedContactPersonId={formData.contact_person_id ?? null}
              selectedContactPersonName={selectedContactPersonSummary}
              onContactChange={handleUnifiedContactChange}
              onLocationChange={handleUnifiedLocationChange}
              onContactPersonChange={handleUnifiedPersonChange}
              onUnifiedSelect={handleUnifiedSelect}
              onLocationCreated={handleUnifiedLocationCreated}
              onPersonCreated={handleUnifiedPersonCreated}
              loadingContacts={loadingContacts}
              loadingLocations={loadingLocations}
              loadingPeople={loadingContactPeople}
              isDark={isDarkMode}
              contactLabel={config.labels.contactLabel}
              contactPlaceholder={isPurchaseDocument ? 'Select supplier...' : 'Select customer...'}
              contactTypes={config.fields.contactTypes?.length ? config.fields.contactTypes : (isPurchaseDocument ? ['supplier'] : ['customer'])}
              documentType={doc_type}
              documentTotal={Number(formData.grand_total || 0)}
              onViewStakeholder={selectedContact ? () => setShowStakeholderOverview(true) : undefined}
              onOnboardStakeholder={() => setShowStakeholderModal(true)}
              onQuickContactCreated={onQuickContactCreated}
              contactRiskFlags={riskAssessment.riskFlags.map((f) => ({ id: f.id, severity: f.severity, title: f.title }))}
            />

            {/* Warehouse selection — kept outside UnifiedContactLookup since it's inventory-specific */}
                {/* WAREHOUSE BLOCK - for inventory operations */}
                {config.fields.showWarehouseLocation && (['invoice', 'sales_order', 'quote', 'delivery_note', 'purchase_order', 'grn'].includes(doc_type)) && (
                  <div className={`p-2 rounded-lg border ${selectedWarehouse
                    ? isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                    : isDarkMode ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
                    }`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${selectedWarehouse
                        ? isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
                        : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                        <Package className={`w-3 h-3 ${selectedWarehouse ? 'text-white' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Warehouse
                        </div>
                        {selectedWarehouse ? (
                          <div className={`text-xs font-semibold truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {selectedWarehouse.location_name}
                          </div>
                        ) : selectedContact ? (
                          <select
                            value={formData.warehouse_id || ''}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              if (!selectedId) {
                                handleWarehouseSelect(null);
                                return;
                              }
                              const warehouse = locations.find(l => String(l.id) === selectedId && l.location_type === 'warehouse');
                              handleWarehouseSelect(warehouse || null);
                            }}
                            className={`w-full text-xs py-0.5 px-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                              }`}
                          >
                            <option value="">Select...</option>
                            {locations.filter(l => l.location_type === 'warehouse').map(warehouse => (
                              <option key={warehouse.id} value={String(warehouse.id)}>
                                {warehouse.location_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-xs text-gray-400">Select contact</div>
                        )}
                      </div>
                      {selectedWarehouse && (
                        <button
                          type="button"
                          onClick={() => handleWarehouseSelect(null)}
                          className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700`}
                        >
                          <X className="w-2.5 h-2.5 text-gray-400" />
                        </button>
                      )}
                    </div>
                    {selectedWarehouse && (
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedWarehouse.address_line1 && (
                          <div className="truncate">{selectedWarehouse.address_line1}</div>
                        )}
                        <div className="truncate">
                          {[selectedWarehouse.city, selectedWarehouse.postal_code].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
          </>
        )}

      </div>

      {/* Right Column - Summary - Always dark for contrast (DIN only) */}
      {showFinanceSummary && (
      <div className="md:col-span-4" data-tour-id="document-totals">
        <FinanceSummaryCard
          isDarkMode={isDarkMode}
          title={(
            <>
              Summary
              {activeCurrency !== 'ZAR' && <span className="ml-1 text-[10px] font-normal">({activeCurrency})</span>}
            </>
          )}
          rows={summaryRows}
          totalValue={formatCurrencyWithSymbol(formData.grand_total ?? 0)}
          footerRows={summaryFooterRows}
          compact={isCompactMobileDoc}
          totalLocked={totalLocked}
          onToggleLock={onToggleLock}
          hasDiscrepancy={hasDiscrepancy}
          onAddBalancingLine={onAddBalancingLine}
        />
      </div>
      )}
    </div>
  );
}

export default PrimaryInfoSection;
